import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Overlay from 'react-bootstrap2/Overlay';
import { popperModifiers } from '__components/helpers';
import { Button } from '@kizen/kds/Button';
import TextInput from '__components/Inputs/TextInput';
import classNames from 'classnames';
import { TypingIndicator } from 'ts-components/Chat/MessageHistory';
import KizenAILogo from './assets/kizen-ai.svg?react';

type AiSinglePromptPopoverProps = {
  onSend: (prompt: string) => void;
  loading: boolean;
  loadingMessages: string[];
  show: boolean;
  intialPrompt: string;
  clearIntialPrompt: () => void;
};

export const AiSinglePromptPopover = ({
  onSend,
  loading,
  loadingMessages,
  intialPrompt,
  clearIntialPrompt,
}: AiSinglePromptPopoverProps) => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [prompt, setPrompt] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const initialRenderRef = useRef(true);
  const intialPromptLoadingRef = useRef(false);

  const buttonRef = useRef(null);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [fade, setFade] = useState(true);

  if (intialPrompt && initialRenderRef.current) {
    initialRenderRef.current = false;
    intialPromptLoadingRef.current = true;
    setShow(true);
    setPrompt(intialPrompt);
    onSend(intialPrompt);
    clearIntialPrompt();
  }

  useEffect(() => {
    if (!loading) {
      if (intialPromptLoadingRef.current) {
        initialRenderRef.current = true;
      }
      return;
    }
    intialPromptLoadingRef.current = true;
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setCurrentMessageIndex((prev) => (prev + 1) % loadingMessages.length);
        setFade(true);
      }, 300);
    }, 3000);

    return () => clearInterval(interval);
  }, [loading, loadingMessages]);

  useEffect(() => {
    if (show && inputRef.current && !loading) {
      inputRef.current.focus();
    }
  }, [show, loading]);

  if (loading && prompt !== '') {
    setPrompt('');
  }

  return (
    <>
      <button
        ref={buttonRef}
        className="bg-transparent border-none text-button-secondary-default hover:text-button-secondary-hover"
        onClick={() => setShow(!show)}
      >
        <KizenAILogo className="h-[30px] w-[30px]" />
      </button>
      <Overlay
        transition={false}
        target={buttonRef.current}
        show={show}
        placement="bottom-start"
        onHide={() => setShow(false)}
        popperConfig={{
          modifiers: [popperModifiers.addStyles({ zIndex: 3 })],
        }}
        rootClose
      >
        <div className="relative w-[300px] bg-background-white border-border-medium border-1 border-solid rounded-[4px] p-[10px] shadow-tile">
          <div className="flex justify-between items-center gap-[10px] align-center justify-center min-h-[42px]">
            <div className="relative w-full h-full">
              <div
                className={classNames(
                  'absolute left-0 right-0 top-[-17px] transition-opacity duration-300',
                  loading ? 'opacity-0 pointer-events-none' : 'opacity-100'
                )}
              >
                <TextInput
                  inputRef={inputRef}
                  placeholder="Create a filter using AI"
                  onChange={setPrompt}
                  value={prompt}
                  onKeyDown={(e: KeyboardEvent) => {
                    if (e.key === 'Enter' && prompt.trim() !== '') {
                      onSend(prompt);
                    }
                  }}
                />
              </div>
              <div
                className={classNames(
                  'absolute left-0 right-0 top-[-5px] pl-[10px] text-center transition-opacity duration-300',
                  loading && fade
                    ? 'opacity-100'
                    : 'opacity-0 pointer-events-none'
                )}
              >
                <TypingIndicator label={loadingMessages[currentMessageIndex]} />
              </div>
            </div>
            <Button
              rightIcon={loading ? 'action-loading' : 'action-send'}
              rightIconSettings={{
                size: 'xl',
                tooltipDescriptionOverride: t('Generate Filter'),
                tooltipEnabled: !loading,
              }}
              variant="text"
              color="secondary"
              size="medium"
              disabled={loading}
              onClick={() => {
                if (prompt.trim() !== '') {
                  onSend(prompt);
                }
              }}
            />
          </div>
        </div>
      </Overlay>
    </>
  );
};
